import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Link, Route, Routes } from 'react-router'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { addDays, format, parseISO } from 'date-fns'
import { ISO_DATE_FORMAT, formatRFC3339DateTime } from '~/shared/lib/date'

import {
  formSubmission,
  taskTemplatesPatients,
  taskV2,
  TaskTemplatesListResponse,
} from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import { useMutation } from '~/shared/hooks/useMutation'
import {
  DateAndRelativeDateInput,
  FormSelect,
  isTaskOption,
  SectionCard,
  StatusPill,
  UserAvatar,
  type FormSelectOption,
  type Tone,
} from '~/shared/ui'
import {
  formatDateTimeDay,
  formatDateTimeFull,
  getFormSubmissionStatus,
  getTaskStatus,
} from './utils'
import FormSubmission from './FormSubmission'
import { Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'

const STATUS_TONE: Record<string, Tone> = {
  Complete: 'success',
  'In Progress': 'warning',
  'Not Started': 'neutral',
  Expired: 'error',
  Incomplete: 'neutral',
}

// Fixed end tracks: the header and each row are separate grid containers.
const FORMS_GRID = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) 11rem 11rem 11rem 11rem 4rem',
  gap: 1.25,
  alignItems: 'center',
} as const

// By default, the Task to complete a FormSubmission should be due today.
const DEFAULT_DUE_DATE_OFFSET = 0

// react-select skin for FormSelect (it spreads caller props after its own
// legacy styles, so this overrides without touching the shared component).
const formSelectStyles = (theme: Theme) => ({
  menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
  control: (base: object, state: { isFocused: boolean }) => ({
    ...base,
    backgroundColor: theme.palette.background.paper,
    fontSize: '1.3rem',
    height: '100%',
    minHeight: '4rem',
    borderRadius: theme.shape.borderRadius,
    borderColor: state.isFocused ? theme.palette.primary.main : theme.palette.divider,
    boxShadow: 'none',
    '&:hover': { borderColor: theme.palette.primary.light },
  }),
  container: (base: object) => ({ ...base, height: '100%' }),
  indicatorSeparator: () => ({ display: 'none' }),
  group: (base: object) => ({ ...base, fontSize: '1.3rem' }),
})

// Design-refresh sibling of FormSubmissionCreator: identical draft semantics
// (form-driven default due date, virtual-task branch, disabled guard) in the
// fixed-height field-row language of the refreshed editors.
const FormSubmissionCreatorRefresh: FC<{
  loading: boolean
  onTaskTemplateSubmit: (template: TaskTemplatesListResponse[number]) => void
  onFormSubmit: (payload: { form: FormSelectOption; dueDate: string }) => void
}> = props => {
  const theme = useTheme()
  const [form, setForm] = useState<FormSelectOption | null>(null)
  const [dueDate, setDueDate] = useState<string>(
    format(addDays(new Date(), DEFAULT_DUE_DATE_OFFSET), ISO_DATE_FORMAT),
  )

  const selectForm = (selected: FormSelectOption | null) => {
    setForm(selected)
    if (!selected) return
    // Only a plain form carries a default offset; a virtual task falls back to today.
    const offset =
      !isTaskOption(selected) && selected.default_due_date_offset != null
        ? selected.default_due_date_offset
        : DEFAULT_DUE_DATE_OFFSET
    setDueDate(format(addDays(new Date(), offset), ISO_DATE_FORMAT))
  }

  const onSubmit = () => {
    if (!form) return
    // Truthiness, not just presence: `collection_id` is nullable, and a template
    // without a collection has always been submitted as a plain form.
    if (isTaskOption(form) && form.collection_id) {
      props.onTaskTemplateSubmit(form)
    } else {
      props.onFormSubmit({ form, dueDate })
    }
  }

  return (
    <SectionCard>
      <Typography sx={{ fontSize: '1.3rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>
        Assign new form
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(7rem, 0.4fr) minmax(0, 1fr) auto',
          gap: 1,
          alignItems: 'stretch',
          height: '4rem',
          // Same bordered-field skin as the refreshed task editor.
          '& .MuiInputBase-root': {
            height: '100%',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: `${theme.shape.borderRadius}px`,
            padding: '0 10px',
            fontSize: '1.3rem',
            alignItems: 'center',
          },
          '& .MuiInputBase-root:hover:not(.Mui-focused)': {
            borderColor: theme.palette.primary.light,
          },
          '& .MuiInputBase-root.Mui-focused': { borderColor: theme.palette.primary.main },
          '& .MuiInputBase-root:before, & .MuiInputBase-root:after': { display: 'none' },
          '& .MuiFormControl-root': { height: '100%' },
        }}
      >
        <FormSelect
          value={form}
          onChange={selectForm}
          styles={formSelectStyles(theme)}
          // The card clips overflow (accent-bar rounding), so the menu must
          // portal out of it — same pattern as UserGroupSelect.
          menuPortalTarget={document.body}
          menuPlacement="auto"
        />
        <DateAndRelativeDateInput value={dueDate} onChange={setDueDate} />
        <Button
          onClick={onSubmit}
          disabled={!form || !dueDate || props.loading}
          variant="contained"
          color="primary"
        >
          Assign to Patient
        </Button>
      </Box>
    </SectionCard>
  )
}

interface PatientDetailFormsProps {
  patient: PatientUser
}

/**
 * Design-refresh Forms tab: the same assignments fetch (virtual tasks + form
 * submissions), assign/delete mutations, link variants, and submission detail
 * route as the legacy tab, with the table re-rendered as card grid rows —
 * status pills, assigner avatar, and a labeled remove action.
 */
const PatientDetailForms: FC<PatientDetailFormsProps> = props => {
  const user = props.patient
  const formSubmissionAssignmentErrorMessage: string = 'Failed to assign form.'

  const [mutated, setMutated] = useState(0)

  const { handler: loadAssignments, result: assignments } = useMutation(
    async () => {
      const [{ data: virtualTasks }, { data: forms }] = await Promise.all([
        taskV2.list({ params: { patient: user.id, source_type: 'virtual' } }),
        formSubmission.list({ params: { user: user.id } }),
      ])
      return [...virtualTasks.results, ...forms]
    },
    { error: 'Failed to fetch assigned tasks and forms' },
  )

  // Re-fetch whenever state mutates, keyed on the counter the mutations below bump.
  useEffect(() => {
    void loadAssignments()
  }, [mutated, loadAssignments])

  const deleteSubmission = useMutation(
    async (payload: NonNullable<Awaited<ReturnType<typeof loadAssignments>>>[number]) => {
      const isVirtualTask = 'collection_id' in payload
      if (isVirtualTask) {
        await taskV2.destroy({ id: payload.id })
      } else {
        await formSubmission.destroy({ id: payload.id })
      }
      setMutated(m => m + 1)
      return true
    },
    { success: 'Assignment removed', error: 'Failed to remove assignment' },
  )

  const createFormSubmission = useMutation(
    async ({ form, dueDate }: { form: FormSelectOption; dueDate: string }) => {
      await formSubmission.create({
        data: {
          user: user.id,
          form: form.id,
          task_due_date: formatRFC3339DateTime(parseISO(dueDate)),
        },
      })

      setMutated(m => m + 1)
      return true
    },
    { error: formSubmissionAssignmentErrorMessage, success: 'Form assigned.' },
  )

  const assignVirtualTask = useMutation(
    async (template: TaskTemplatesListResponse[number]) => {
      await taskTemplatesPatients.create({
        template_id: template.id,
        patient_id: user.id,
      })
      setMutated(m => m + 1)
      return true
    },
    { success: 'Virtual task assigned.', error: 'Failed to assign virtual task.' },
  )

  const sortedAssignments = (assignments ?? []).sort(
    (a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime(),
  )

  const nameFor = (submission: (typeof sortedAssignments)[number]) => {
    const isVirtualTask = 'collection_id' in submission
    if (isVirtualTask) return submission.title
    if (!('form' in submission && submission.form.submission_viewable)) {
      return 'form' in submission ? submission.form.title : 'Untitled'
    }
    if (
      submission.metadata &&
      typeof submission.metadata === 'object' &&
      'url' in submission.metadata &&
      typeof submission.metadata.url === 'string' &&
      submission.metadata.url &&
      submission.form.submission_viewable
    ) {
      return (
        <a href={submission.metadata.url} target="_blank" rel="noopener noreferrer">
          {submission.form.title}
        </a>
      )
    }
    if (submission.form.submission_viewable) {
      return (
        <Link to={`/patients/${user.id}/forms/submissions/${submission.id}`}>
          {submission.form.title}
        </Link>
      )
    }
    return null
  }

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <Routes>
        <Route
          index
          element={
            <>
              <FormSubmissionCreatorRefresh
                loading={createFormSubmission.loading || assignVirtualTask.loading}
                onTaskTemplateSubmit={assignVirtualTask.run}
                onFormSubmit={createFormSubmission.run}
              />

              <Box sx={{ mt: 2 }}>
                <SectionCard disablePadding>
                  <Box
                    sx={{
                      ...FORMS_GRID,
                      px: 2,
                      py: 1,
                      bgcolor: 'background.cardFooter',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: 'text.secondary',
                    }}
                  >
                    <span>Form</span>
                    <span>Status</span>
                    <span>Assigned by</span>
                    <span>Assigned</span>
                    <span>Completed</span>
                    <span />
                  </Box>
                  {sortedAssignments.map(submission => {
                    const isVirtualTask = 'collection_id' in submission
                    const status = isVirtualTask
                      ? getTaskStatus({ isComplete: !!submission.is_complete })
                      : getFormSubmissionStatus({
                          completedAt: submission.completed_at,
                          expiredAt: submission.expired_at,
                          startedAt: submission.started_at,
                        })
                    const isCompleted = 'completed_at' in submission && submission.completed_at
                    const isDeletable =
                      !isCompleted &&
                      !(!isVirtualTask && submission.form.title == 'Insurance Information')
                    return (
                      <Box
                        key={submission.id}
                        sx={theme => ({
                          ...FORMS_GRID,
                          px: 2,
                          py: 1.25,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        })}
                      >
                        <Typography
                          component="div"
                          sx={{
                            fontSize: '1.35rem',
                            fontWeight: 500,
                            color: 'text.primary',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {nameFor(submission)}
                        </Typography>
                        <Box>
                          <StatusPill tone={STATUS_TONE[status] ?? 'neutral'} label={status} />
                        </Box>
                        <Box>
                          {submission.created_by ? (
                            <UserAvatar
                              id={submission.created_by}
                              size="small"
                              avatarStyle={{ margin: 0 }}
                            />
                          ) : null}
                        </Box>
                        <Typography
                          sx={{ fontSize: '1.25rem', color: 'text.secondary' }}
                          title={formatDateTimeFull(submission.created_at)}
                        >
                          {formatDateTimeDay(submission.created_at)}
                        </Typography>
                        <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
                          {isCompleted ? formatDateTimeDay(submission.completed_at as string) : '–'}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          {isDeletable ? (
                            <IconButton
                              size="small"
                              aria-label="Remove assignment"
                              onClick={() => deleteSubmission.run(submission)}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: '1.8rem' }} />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    )
                  })}
                </SectionCard>
              </Box>
            </>
          }
        />

        <Route path="submissions/:submissionId/*" element={<FormSubmission user={user} />} />
      </Routes>
    </Box>
  )
}

export default PatientDetailForms
